// API Service for Didohurtownia E-Commerce Frontend
// This service provides methods to interact with the backend API

import * as Sentry from '@sentry/react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

/**
 * Get JWT token from localStorage
 * Security: Token stored in localStorage for authentication
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

/**
 * Get authentication headers with JWT token
 * Security: Adds Bearer token to requests for authenticated endpoints
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Transform backend order format to frontend format
 * Backend now returns: { customer: { name, email, ... }, items: [...] }
 * This function handles both new nested format and legacy flat format for backwards compatibility
 * Security: No sensitive data transformation, just structural mapping
 */
const transformOrderFromBackend = (order: any): any => {
  if (!order) return order;
  
  // Check if customer is already in nested format (new backend format)
  const hasNestedCustomer = order.customer && typeof order.customer === 'object';
  
  // Transform items from backend format to frontend format if needed
  // Backend may send: { product_id, product_name, product_price, quantity, subtotal }
  // Frontend expects: { id, name, price, quantity }
  const transformedItems = order.items?.map((item: any) => ({
    id: item.product_id || item.id,
    name: item.product_name || item.name,
    price: parseFloat(item.product_price || item.price || 0),
    quantity: item.quantity,
    subtotal: parseFloat(item.subtotal || (item.price * item.quantity) || 0),
    image: item.product_image || item.image || undefined,
    slug: item.product_slug || item.slug || undefined,
  })) || [];
  
  return {
    id: order.id,
    // Use nested customer if available, otherwise create from flat fields
    customer: hasNestedCustomer ? order.customer : {
      name: order.customer_name || '',
      email: order.customer_email || '',
      address: order.customer_address || '',
      city: order.customer_city || '',
      zip: order.customer_zip || '',
      taxNumber: order.customer_tax_number || undefined,
    },
    items: transformedItems,
    total: parseFloat(order.total || 0),
    subtotal: parseFloat(order.subtotal || 0),
    vat: parseFloat(order.vat || 0),
    status: order.status || 'pending',
    // Transform date fields (backend uses created_at, frontend expects date)
    date: order.date || order.created_at || new Date().toISOString(),
    notes: order.notes || undefined,
    trackingNumber: order.tracking_number || order.trackingNumber || undefined,
  };
};

// Generic fetch wrapper with error handling
async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminUser');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
      throw new Error('Unauthorized');
    }

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || 'API request failed');
      (error as any).response = { status: response.status };
      throw error;
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    Sentry.captureException(error);
    throw error;
  }
}

// Generic fetch wrapper with authentication for admin endpoints
// Security: Automatically adds JWT token for authenticated requests
async function fetchApiAuth<T>(
  endpoint: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  return fetchApi<T>(endpoint, {
    ...options,
    headers: {
      ...options?.headers,
      ...getAuthHeaders(),
    },
  });
}

// Products API
export const productsApi = {
  getAll: (params?: { category?: string; search?: string; featured?: boolean; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.append('category', params.category);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.featured !== undefined) searchParams.append('featured', String(params.featured));
    if (params?.page !== undefined) searchParams.append('page', String(params.page));
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit));
    
    const query = searchParams.toString();
    return fetchApi(`/products${query ? `?${query}` : ''}`);
  },

  getById: (id: string) => fetchApi(`/products/${id}`),

  create: (product: any) =>
    fetchApiAuth('/products', {
      method: 'POST',
      body: JSON.stringify(product),
    }),

  update: (id: string, product: any) =>
    fetchApiAuth(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(product),
    }),

  delete: (id: string) =>
    fetchApiAuth(`/products/${id}`, {
      method: 'DELETE',
    }),

  updateStock: (id: string, quantity: number) =>
    fetchApiAuth(`/products/${id}/stock`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity }),
    }),
};

// Categories API
export const categoriesApi = {
  getAll: () => fetchApi('/categories'),

  getById: (id: string) => fetchApi(`/categories/${id}`),

  create: (category: any) =>
    fetchApiAuth('/categories', {
      method: 'POST',
      body: JSON.stringify(category),
    }),

  update: (id: string, category: any) =>
    fetchApiAuth(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(category),
    }),

  delete: (id: string) =>
    fetchApiAuth(`/categories/${id}`, {
      method: 'DELETE',
    }),
};

// Orders API
export const ordersApi = {
  getAll: async (params?: { status?: string; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.search) searchParams.append('search', params.search);
    
    const query = searchParams.toString();
    const response = await fetchApiAuth(`/orders${query ? `?${query}` : ''}`);
    
    // Transform orders from backend format to frontend format
    if (response?.data && Array.isArray(response.data)) {
      response.data = response.data.map(transformOrderFromBackend);
    }
    
    return response;
  },

  getById: async (id: string) => {
    const response = await fetchApiAuth(`/orders/${id}`);
    
    // Transform order from backend format to frontend format
    if (response?.data) {
      response.data = transformOrderFromBackend(response.data);
    }
    
    return response;
  },

  create: async (order: any) => {
    const response = await fetchApi('/orders', {
      method: 'POST',
      body: JSON.stringify(order),
    });
    
    // Transform created order from backend format to frontend format
    if (response?.data) {
      response.data = transformOrderFromBackend(response.data);
    }
    
    return response;
  },

  update: async (id: string, order: any) => {
    const response = await fetchApiAuth(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(order),
    });
    
    // Transform updated order from backend format to frontend format
    if (response?.data) {
      response.data = transformOrderFromBackend(response.data);
    }
    
    return response;
  },

  updateStatus: async (id: string, status: string) => {
    const response = await fetchApiAuth(`/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    
    // Transform updated order from backend format to frontend format
    if (response?.data) {
      response.data = transformOrderFromBackend(response.data);
    }
    
    return response;
  },

  delete: (id: string) =>
    fetchApiAuth(`/orders/${id}`, {
      method: 'DELETE',
    }),

  getAnalytics: () => fetchApiAuth('/orders/analytics'),
};

// Health check
export const healthCheck = () =>
  fetch(`${API_BASE_URL.replace('/api', '')}/health`).then((res) => res.json());

// Media Library API
export const mediaApi = {
  getAll: () => fetchApi('/media'),

  getById: (id: string) => fetchApi(`/media/${id}`),

  create: (media: any) =>
    fetchApiAuth('/media', {
      method: 'POST',
      body: JSON.stringify(media),
    }),

  upload: async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/media/upload`, {
      method: 'POST',
      headers,
      body: formData,
      // Don't set Content-Type header, browser will set it with boundary
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Upload failed');
    }
    
    return data;
  },

  uploadMultiple: async (files: File[]) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });
    
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/media/upload-multiple`, {
      method: 'POST',
      headers,
      body: formData,
      // Don't set Content-Type header, browser will set it with boundary
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Upload failed');
    }
    
    return data;
  },

  delete: (id: string) =>
    fetchApiAuth(`/media/${id}`, {
      method: 'DELETE',
    }),

  search: (query: string) => fetchApi(`/media/search?q=${encodeURIComponent(query)}`),
};

// Auth API
export const authApi = {
  login: (credentials: { username: string; password: string }) =>
    fetchApi('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  verify: (token: string) =>
    fetchApi('/auth/verify', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),
};
